import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import DashboardLayout from "layouts/dashboard/DashboardLayout";
import InputField from "components/fields/InputField";
import { changePassword } from "api/auth";
import { useAuthStore } from "store/authStore";

const NEW_PASSWORD_RULE =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

const RULES_LIST_ID = "change-password-new-rules";
const FORM_ERROR_ID = "change-password-form-error";

function validateNewPassword(pw: string): string | null {
  if (!pw.trim()) return "New password is required.";
  if (pw.length < 8) return "New password must be at least 8 characters.";
  if (!NEW_PASSWORD_RULE.test(pw)) {
    return "New password must include uppercase, lowercase, a number, and a special character.";
  }
  return null;
}

export default function ChangePasswordView(): JSX.Element {
  const navigate = useNavigate();
  const clearAuth = useAuthStore((s) => s.clearAuth);

  const currentRef = useRef<HTMLInputElement>(null);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [fieldErrors, setFieldErrors] = useState<{
    currentPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    currentRef.current?.focus();
  }, []);

  const runClientValidation = (): boolean => {
    const next: typeof fieldErrors = {};
    if (!currentPassword.trim()) {
      next.currentPassword = "Current password is required.";
    }
    const npErr = validateNewPassword(newPassword);
    if (npErr) next.newPassword = npErr;
    if (!confirmPassword.trim()) {
      next.confirmPassword = "Confirm password is required.";
    } else if (confirmPassword !== newPassword) {
      next.confirmPassword = "Passwords do not match.";
    }
    setFieldErrors(next);
    setFormError("");
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (!runClientValidation()) return;

    setSubmitting(true);
    setFormError("");

    try {
      const message = await changePassword({
        currentPassword,
        newPassword,
        confirmPassword,
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setFieldErrors({});
      toast.success(message);
      clearAuth();
      navigate("/auth/sign-in", { replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unable to change password";
      setFormError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout headerTitle="Change password">
      <main className="flex-1 overflow-y-auto p-3 sm:p-6">
        <div className="mx-auto max-w-lg rounded-lg border border-[#EEEEEE] bg-white p-4 sm:p-6">
          <div className="mb-5">
            <h1 className="text-[20px] font-bold text-[#222]">Change password</h1>
            <p className="mt-1 text-[13px] text-[#6D6D6D]">
              Super Admin only. After a successful change, all sessions end and you will sign in again.
            </p>
          </div>

          <form
            onSubmit={(e) => void onSubmit(e)}
            className="flex flex-col gap-4"
            noValidate
            aria-describedby={formError ? FORM_ERROR_ID : undefined}
          >
            {!!formError && (
              <div
                id={FORM_ERROR_ID}
                role="alert"
                className="rounded-md border border-[#FFD5D8] bg-[#FFF1F2] px-3 py-2 text-[13px] text-[#FB2C36]"
              >
                {formError}
              </div>
            )}
            <InputField
              ref={currentRef}
              variant="auth"
              extra=""
              label="Current password"
              placeholder="Enter current password"
              id="change-password-current"
              name="currentPassword"
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              state={fieldErrors.currentPassword ? "error" : undefined}
              aria-invalid={!!fieldErrors.currentPassword}
              aria-describedby={
                fieldErrors.currentPassword
                  ? "err-change-password-current"
                  : undefined
              }
            />
            {fieldErrors.currentPassword ? (
              <p id="err-change-password-current" className="-mt-2 text-xs text-red-600" role="alert">
                {fieldErrors.currentPassword}
              </p>
            ) : null}

            <div>
              <InputField
                variant="auth"
                extra=""
                label="New password"
                placeholder="Strong new password"
                id="change-password-new"
                name="newPassword"
                type="password"
                autoComplete="new-password"
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                state={fieldErrors.newPassword ? "error" : undefined}
                aria-invalid={!!fieldErrors.newPassword}
                aria-describedby={
                  [RULES_LIST_ID, fieldErrors.newPassword ? "err-change-password-new" : ""]
                    .filter(Boolean)
                    .join(" ") || undefined
                }
              />
              <ul
                id={RULES_LIST_ID}
                className="mt-2 list-inside list-disc text-[11px] leading-relaxed text-[#6D6D6D]"
              >
                <li>At least 8 characters</li>
                <li>One uppercase and one lowercase letter</li>
                <li>One number and one special character</li>
              </ul>
              {fieldErrors.newPassword ? (
                <p id="err-change-password-new" className="mt-1 text-xs text-red-600" role="alert">
                  {fieldErrors.newPassword}
                </p>
              ) : null}
            </div>

            <InputField
              variant="auth"
              extra=""
              label="Confirm new password"
              placeholder="Re-enter new password"
              id="change-password-confirm"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              state={fieldErrors.confirmPassword ? "error" : undefined}
              aria-invalid={!!fieldErrors.confirmPassword}
              aria-describedby={
                fieldErrors.confirmPassword ? "err-change-password-confirm" : undefined
              }
            />
            {fieldErrors.confirmPassword ? (
              <p id="err-change-password-confirm" className="-mt-2 text-xs text-red-600" role="alert">
                {fieldErrors.confirmPassword}
              </p>
            ) : null}

            <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="order-2 h-10 rounded-md border border-[#EEEEEE] px-4 text-[12px] font-semibold text-[#222] transition-colors hover:bg-[#F7F7F7] sm:order-1"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                aria-busy={submitting}
                className="order-1 h-10 rounded-md bg-[#222] px-4 text-[12px] font-semibold text-white transition-colors hover:bg-[#333] disabled:opacity-50 sm:order-2"
              >
                {submitting ? "Updating…" : "Update password"}
              </button>
            </div>
          </form>
        </div>
      </main>
    </DashboardLayout>
  );
}
